// The copilot's bug reporter / idea box / feedback / support form — built to match Chirply's.
// Someone can report from whatever screen they're on without leaving it: capture the screen in one
// click (the assistant hides itself for the grab), paste a snip straight into the details, drop or
// pick files, then crop / black out / mark up each image before it sends. Typed words survive a
// close or reload (drafts.ts); on success it shows a receipt instead of navigating away.
import { useEffect, useState, type ClipboardEvent } from "react";
import { Bug, Camera, Check, ClipboardPaste, Heart, Lightbulb, Loader2, MapPin, MessageCircle } from "lucide-react";
import { BUG_SEVERITIES, BUG_SEVERITY_LABELS, type BugSeverity } from "@gmx/shared/support-files";
import { api } from "../../lib/api";
import { AttachmentPicker, useFileDrop, type AttachmentSelection } from "../support/Attachments";
import { ImageEditor } from "../support/ImageEditor";
import { clipboardImages, replaceFile, snipShortcut, supportForm } from "../support/attachment-rules";
import { EMPTY_DRAFT, applyDraft, clearDraft, readDraft, saveDraft, type DraftKind } from "../support/drafts";
import { isScreenshotSupported, type CaptureOutcome } from "../support/screenshot";

export type ReportKind = DraftKind;

export const REPORT_COPY: Record<ReportKind, { tab: string; label: string; hint: string; icon: typeof Bug; intro: string; titleLabel: string; titlePlaceholder: string; bodyLabel: string; bodyPlaceholder: string; submit: string; thanks: string }> = {
  bug: {
    tab: "Bug",
    label: "Report a bug",
    hint: "Something not working?",
    icon: Bug,
    intro: "Show us what broke — a screenshot makes it far quicker to fix.",
    titleLabel: "What's broken?",
    titlePlaceholder: "Scheduling a reel spins forever",
    bodyLabel: "What happened?",
    bodyPlaceholder: "What you did, what you expected, and what actually happened. Paste a screenshot right here.",
    submit: "Send report",
    thanks: "Thanks — the team has your report and will pick it up from here.",
  },
  feature: {
    tab: "Idea",
    label: "Suggest a feature",
    hint: "Make room for your ideas",
    icon: Lightbulb,
    intro: "What would you love Grow My X to do? Mockups and screenshots welcome.",
    titleLabel: "What would you like?",
    titlePlaceholder: "Organize replies to my X posts",
    bodyLabel: "Tell us more",
    bodyPlaceholder: "The outcome you're after and the problem it solves.",
    submit: "Send idea",
    thanks: "Got it — your idea is with the team.",
  },
  feedback: {
    tab: "Feedback",
    label: "Share feedback",
    hint: "Tell us how it feels",
    icon: Heart,
    intro: "What's working well, and what could feel better?",
    titleLabel: "In a few words",
    titlePlaceholder: "The new home dashboard",
    bodyLabel: "Your feedback",
    bodyPlaceholder: "Point at anything with a screenshot — paste it right here.",
    submit: "Send feedback",
    thanks: "Thank you — the team reads every one of these.",
  },
  support: {
    tab: "Help",
    label: "Get support",
    hint: "A little help from our team",
    icon: MessageCircle,
    intro: "Tell us where you need a hand. Our team will reply in My requests.",
    titleLabel: "What do you need help with?",
    titlePlaceholder: "Connecting my second X account",
    bodyLabel: "Details",
    bodyPlaceholder: "What you're trying to do and where you got stuck. Screenshots help.",
    submit: "Ask the team",
    thanks: "Sent — we'll reply right here in My requests.",
  },
};
export const REPORT_KINDS = Object.keys(REPORT_COPY) as ReportKind[];

export function ReportForm({
  kind,
  onKindChange,
  userId,
  page,
  igAccountId,
  accountLabel,
  prefillTitle = "",
  clientInfo,
  attachments,
  onCapture,
  onViewRequests,
  onSubmitted,
}: {
  kind: ReportKind;
  onKindChange: (kind: ReportKind) => void;
  userId: string;
  page: string;
  igAccountId: string | null;
  accountLabel: string;
  /** A title suggested by where the form was opened from ("Help with: <article>"). Never overwrites typing. */
  prefillTitle?: string;
  clientInfo: () => string;
  /** Owned by the assistant so screenshots survive closing and reopening it (words survive reloads via drafts). */
  attachments: AttachmentSelection;
  /** Capture the screen with the assistant hidden for the grab. */
  onCapture: () => Promise<CaptureOutcome>;
  onViewRequests: () => void;
  onSubmitted?: () => void;
}) {
  const [initial] = useState(() => applyDraft(EMPTY_DRAFT, readDraft(kind, userId)));
  const [title, setTitle] = useState(initial.title || prefillTitle);
  const [description, setDescription] = useState(initial.description);
  const [severity, setSeverity] = useState<BugSeverity>(initial.severity);
  const [editing, setEditing] = useState<{ file: File; index: number | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [shooting, setShooting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState<{ id: string; kind: ReportKind } | null>(null);
  const [canShoot] = useState(isScreenshotSupported);
  const [snip] = useState(() => snipShortcut());
  const drop = useFileDrop(attachments.add, busy);
  const copy = REPORT_COPY[kind];

  // Switching kind keeps what's typed and only fills EMPTY fields from that kind's draft.
  const [lastKind, setLastKind] = useState(kind);
  if (lastKind !== kind) {
    setLastKind(kind);
    const merged = applyDraft({ title, description, severity, savedAt: 0 }, readDraft(kind, userId));
    setTitle(merged.title);
    setDescription(merged.description);
    setSeverity(merged.severity);
  }
  const [lastPrefill, setLastPrefill] = useState(prefillTitle);
  if (lastPrefill !== prefillTitle) {
    setLastPrefill(prefillTitle);
    if (prefillTitle && !title.trim()) setTitle(prefillTitle);
  }
  useEffect(() => {
    if (!sent) saveDraft(kind, userId, { title, description, severity });
  }, [kind, userId, title, description, severity, sent]);

  async function capture() {
    if (shooting || busy) return;
    setShooting(true);
    setError("");
    try {
      const outcome = await onCapture();
      // A fresh capture goes to the editor first — never attach a raw, un-reviewed frame.
      if (outcome.status === "captured") setEditing({ file: outcome.file, index: null });
      else if (outcome.status === "error") setError(outcome.message);
      else if (outcome.status === "unsupported") setError(`This browser can't capture the screen — snip it (${snip}) and paste it into the details.`);
    } finally {
      setShooting(false);
    }
  }

  function paste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const images = clipboardImages(e.clipboardData);
    if (!images.length) return; // an ordinary text paste
    e.preventDefault();
    // The first pasted image opens in the editor; any extras attach straight away.
    const [first, ...rest] = images;
    attachments.add(rest);
    setEditing({ file: first, index: null });
  }

  async function submit() {
    if (busy) return;
    if (title.trim().length < 3) return setError("Give it a short title (at least 3 characters).");
    if (description.trim().length < 3) return setError(kind === "bug" ? "Describe what happened so we can reproduce it." : "Add a few more details.");
    setBusy(true);
    setError("");
    try {
      const body = supportForm(
        { kind, title: title.trim(), description: description.trim(), page, igAccountId: igAccountId ?? "", severity: kind === "bug" ? severity : undefined, clientInfo: clientInfo().slice(0, 300) },
        attachments.files,
      );
      const r = await api<{ id: string }>("/api/support", { method: "POST", body });
      // Only now is it safe to drop the copy: every failure above leaves the words and files in place.
      // Switching kind carries the words along, so the same text may sit under another kind too.
      for (const k of REPORT_KINDS) {
        const d = readDraft(k, userId);
        if (k === kind || (d && d.title.trim() === title.trim() && d.description.trim() === description.trim())) clearDraft(k, userId);
      }
      setTitle("");
      setDescription("");
      setSeverity("normal");
      attachments.clear();
      setSent({ id: r.id, kind });
      onSubmitted?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "That couldn't be sent — try again.");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="gf-report gf-report--sent">
        <span className="gf-report__check">
          <Check size={22} />
        </span>
        <p>{REPORT_COPY[sent.kind].thanks}</p>
        <small>Request {sent.id.slice(0, 8)}</small>
        <button type="button" className="gf-report__primary" onClick={onViewRequests}>
          View in My requests
        </button>
        <button type="button" onClick={() => setSent(null)}>
          Report something else
        </button>
      </div>
    );
  }

  return (
    <form
      className="gf-report"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="gf-report__kinds" role="group" aria-label="What kind of request">
        {REPORT_KINDS.map((k) => {
          const Icon = REPORT_COPY[k].icon;
          return (
            <button key={k} type="button" aria-pressed={kind === k} onClick={() => { onKindChange(k); setError(""); }}>
              <Icon size={14} /> {REPORT_COPY[k].tab}
            </button>
          );
        })}
      </div>
      <h2>{copy.label}</h2>
      <p className="gf-report__intro">{copy.intro}</p>
      <label>
        {copy.titleLabel}
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} placeholder={copy.titlePlaceholder} disabled={busy} autoFocus />
      </label>
      {kind === "bug" && (
        <label>
          How bad is it?
          <select value={severity} onChange={(e) => setSeverity(e.target.value as BugSeverity)} disabled={busy}>
            {BUG_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {BUG_SEVERITY_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        {copy.bodyLabel}
        <textarea
          className={drop.over ? "is-over" : undefined}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onPaste={paste}
          {...drop.props}
          maxLength={12000}
          rows={5}
          placeholder={copy.bodyPlaceholder}
          disabled={busy}
        />
      </label>
      <div className="gf-report__shots">
        {canShoot && (
          <button type="button" className="gf-report__capture" onClick={() => void capture()} disabled={shooting || busy}>
            {shooting ? <Loader2 size={15} className="gf-report__spin" /> : <Camera size={15} />}
            {shooting ? "Capturing…" : "Capture screenshot"}
          </button>
        )}
        <p className="gf-report__hint">
          <ClipboardPaste size={13} aria-hidden />
          <span>
            Or snip a region (<kbd>{snip}</kbd>) and paste it into the box above.
          </span>
        </p>
        <AttachmentPicker
          files={attachments.files}
          error={attachments.error}
          onAdd={attachments.add}
          onRemove={attachments.remove}
          onEdit={(i) => setEditing({ file: attachments.files[i], index: i })}
          disabled={busy}
        />
        <p className="gf-report__fine">You can crop, black out and mark up any image before it sends.</p>
      </div>
      {(kind === "bug" || kind === "support") && (
        <p className="gf-report__where">
          <MapPin size={12} aria-hidden />
          <span>
            Reporting from <strong>{page}</strong>
            {accountLabel ? <> · {accountLabel}</> : null}
          </span>
        </p>
      )}
      {error && (
        <p role="alert" className="gf-report__error">
          {error}
        </p>
      )}
      <button type="submit" className="gf-report__primary" disabled={busy}>
        {busy ? <Loader2 size={15} className="gf-report__spin" /> : <copy.icon size={15} />}
        {busy ? "Sending…" : copy.submit}
      </button>
      <p className="gf-report__fine">Goes straight to the Grow My X team. Leave passwords and API keys out.</p>
      {editing && (
        <ImageEditor
          file={editing.file}
          onCancel={() => setEditing(null)}
          onDone={(edited) => {
            if (editing.index === null) attachments.add([edited]);
            else attachments.update((current) => replaceFile(current, editing.index!, edited));
            setEditing(null);
          }}
        />
      )}
    </form>
  );
}
